import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import useApiClient from '~/src/hooks/useApiClient';
import { currentFolderState } from '~/src/ProjectFolder/store';
import { ENTRIES_PER_PAGE } from '~/src/Projects/constants';
import {
  Entry,
  needRestartProjectsPageState,
  pendingFind,
  projectListState,
} from '~/src/Projects/store';
import { snackbarMessageSelector } from '~/src/Snackbar/store';
import { queryFilterSelector, SearchParams } from '../store';

const useProjectsSearch = () => {
  const { t } = useTranslation('projects');
  const apiClient = useApiClient();
  const setSnackbarMessage = useSetRecoilState(snackbarMessageSelector);
  const setPending = useSetRecoilState(pendingFind);
  const setProjects = useSetRecoilState(projectListState);
  const currentFolder = useRecoilValue(currentFolderState);
  const setNeedRestartProjectsPage = useSetRecoilState(
    needRestartProjectsPageState,
  );
  const filters = useRecoilValue(queryFilterSelector);

  const getFilteredProjects = async (searchString = '') => {
    try {
      setPending(true);
      const params: SearchParams = {
        limit: ENTRIES_PER_PAGE,
        skip: 0,
        query: searchString,
      };

      let requestFilters;

      switch (true) {
        case !!filters && !currentFolder:
          requestFilters = filters;
          break;
        case !filters && !!currentFolder:
          requestFilters = { folderId: currentFolder?._id };
          break;
        case !!filters && !!currentFolder:
          requestFilters = {
            folderId: currentFolder?._id,
            ...(filters || {}),
          };
          break;
        default:
          requestFilters = {};
      }

      params.filter = btoa(JSON.stringify(requestFilters));

      const {
        data: { data, pagination },
      } = await apiClient.get<{
        data: Entry[];
        pagination: { hasMore: boolean };
      }>('/search', { params });
      if (data) {
        setProjects({
          entries: data,
          hasMore: pagination?.hasMore || false,
        });
        setNeedRestartProjectsPage(true);
      }
    } catch (err) {
      setSnackbarMessage({
        message: t('filterProjects.errors.search'),
        severity: 'error',
      });
    } finally {
      setPending(false);
    }
  };

  return [getFilteredProjects];
};

export default useProjectsSearch;
